import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import auth from '~/auth';

import { Alert, Box, Container, Typography } from '@mui/material';

import ExpiredTokenMessage from './Auth/Messages/ExpiredTokenMessage';
import SocialGoogleButton from './Auth/SocialLogin/SocialGoogleButton';

function LoginPage() {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [showExpiredTokenMessage, setShowExpiredTokenMessage] = useState(false);
  const [isProcessingCredential, setIsProcessingCredential] = useState(false);
  const history = useHistory();

  useEffect(() => {
    setShowExpiredTokenMessage(auth.consumeNotice()?.type === 'session-expired');
  }, []);

  // Handle credential from URL for redirect flows (review/canary apps using shared auth domain).
  // When redirected back from the shared auth domain, the URL contains ?credential=JWT.
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get('credential')) {
      return;
    }

    setIsProcessingCredential(true);

    void auth
      .completeLoginIfNeeded()
      .then(() => {
        history.push('/');
      })
      .catch((err) => {
        setIsProcessingCredential(false);
        setErrorMessage(err instanceof Error ? err.message : 'Google login failed');
      });
  }, [history]);

  // Show a loading state while processing credential from shared auth domain redirect
  if (isProcessingCredential) {
    return (
      <Container>
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography>Signing in with Google...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      {showExpiredTokenMessage && <ExpiredTokenMessage />}
      <Helmet>
        <title>Login</title>
      </Helmet>

      <Box className="row flex-centered">
        <Box sx={{ mt: 6 }}>
          <Typography variant="h1" className="page-header">
            Login
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 360 }}>
            <Typography>Sign in with your Google account to access the admin portal.</Typography>

            {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}

            {window.configs.GOOGLE_LOGIN_ENABLED === 'true' &&
            (window.google || window.configs.GOOGLE_AUTH_REDIRECT_DOMAIN) ? (
              <SocialGoogleButton onError={setErrorMessage} />
            ) : (
              <Alert severity="info">Google login is currently unavailable for this environment.</Alert>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default LoginPage;
